"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import statesCountiesData from "@/assets/data/us-states-counties.json";
import styles from "./CountyStateFilter.module.css";

interface CountyStateFilterProps {
	value?: string; // Format: "County, State" (e.g., "Wake, North Carolina")
	onChange?: (value: string) => void;
}

const INITIAL_LOAD_COUNT = 50;
const LOAD_MORE_COUNT = 50;

export default function CountyStateFilter({
	value,
	onChange,
}: CountyStateFilterProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [displayedCount, setDisplayedCount] = useState(INITIAL_LOAD_COUNT);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

	// Generate all county-state combinations (only once)
	const allOptions = useMemo(() => {
		const options: Array<{ value: string; label: string }> = [];

		statesCountiesData.forEach((stateData) => {
			stateData.counties.forEach((county) => {
				// Remove " County" suffix if present (e.g., "Wake County" -> "Wake")
				// Also handle variations like "County (City and County)" -> just the name
				const countyName = county
					.replace(/\s+County\s*\(.*?\)$/, "") // Remove "County (City and County)"
					.replace(/\s+County$/, ""); // Remove " County"
				// Format: "County, State" (e.g., "Wake, North Carolina")
				const optionValue = `${countyName}, ${stateData.state}`;
				options.push({
					value: optionValue,
					label: optionValue,
				});
			});
		});
		// Sort alphabetically
		return options.sort((a, b) => a.label.localeCompare(b.label));
	}, []);

	// Filter options based on search query
	const filteredOptions = useMemo(() => {
		if (!searchQuery.trim()) {
			return allOptions;
		}
		const query = searchQuery.toLowerCase().trim();
		return allOptions.filter((option) =>
			option.label.toLowerCase().includes(query)
		);
	}, [allOptions, searchQuery]);

	// Get displayed options (limited count)
	const displayedOptions = useMemo(() => {
		return filteredOptions.slice(0, displayedCount);
	}, [filteredOptions, displayedCount]);

	const hasMore = displayedOptions.length < filteredOptions.length;

	// Reset displayed count and highlighted index when search changes
	useEffect(() => {
		setDisplayedCount(INITIAL_LOAD_COUNT);
		setHighlightedIndex(-1);
		// Reset option refs
		optionRefs.current = [];
	}, [searchQuery]);

	// Reset highlighted index when dropdown opens
	useEffect(() => {
		if (isOpen) {
			setHighlightedIndex(-1);
			optionRefs.current = [];
		}
	}, [isOpen]);

	// Update option refs array size when displayed options change
	useEffect(() => {
		optionRefs.current = optionRefs.current.slice(
			0,
			displayedOptions.length
		);
	}, [displayedOptions.length]);

	// Handle scroll to load more
	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const target = e.currentTarget;
		const scrollBottom =
			target.scrollHeight - target.scrollTop - target.clientHeight;

		// Load more when within 100px of bottom
		if (scrollBottom < 100 && hasMore) {
			setDisplayedCount((prev) => prev + LOAD_MORE_COUNT);
		}
	};

	// Handle option selection
	const handleSelect = (optionValue: string) => {
		onChange?.(optionValue);
		setIsOpen(false);
		setSearchQuery("");
		setDisplayedCount(INITIAL_LOAD_COUNT);
		setHighlightedIndex(-1);
	};

	// Scroll highlighted option into view
	useEffect(() => {
		if (
			highlightedIndex >= 0 &&
			optionRefs.current[highlightedIndex] &&
			scrollContainerRef.current
		) {
			const optionElement = optionRefs.current[highlightedIndex];
			const container = scrollContainerRef.current;

			const optionTop = optionElement.offsetTop;
			const optionBottom = optionTop + optionElement.offsetHeight;
			const containerTop = container.scrollTop;
			const containerBottom = containerTop + container.clientHeight;

			// Scroll if option is above visible area
			if (optionTop < containerTop) {
				container.scrollTo({
					top: optionTop - 10,
					behavior: "smooth",
				});
			}
			// Scroll if option is below visible area
			else if (optionBottom > containerBottom) {
				container.scrollTo({
					top: optionBottom - container.clientHeight + 10,
					behavior: "smooth",
				});
			}
		}
	}, [highlightedIndex]);

	// Handle keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!isOpen) {
			if (
				e.key === "Enter" ||
				e.key === "ArrowDown" ||
				e.key === "ArrowUp"
			) {
				e.preventDefault();
				setIsOpen(true);
				return;
			}
			return;
		}

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setHighlightedIndex((prev) => {
					const nextIndex =
						prev < displayedOptions.length - 1 ? prev + 1 : prev;
					// Load more if near the end
					if (
						nextIndex >= displayedCount - 5 &&
						hasMore &&
						nextIndex === displayedCount - 1
					) {
						setDisplayedCount((count) => count + LOAD_MORE_COUNT);
					}
					return nextIndex;
				});
				break;

			case "ArrowUp":
				e.preventDefault();
				setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
				break;

			case "Enter":
				e.preventDefault();
				if (
					highlightedIndex >= 0 &&
					highlightedIndex < displayedOptions.length
				) {
					handleSelect(displayedOptions[highlightedIndex].value);
				} else if (displayedOptions.length > 0) {
					// If nothing is highlighted, select the first option
					handleSelect(displayedOptions[0].value);
				}
				break;

			case "Escape":
				e.preventDefault();
				setIsOpen(false);
				setSearchQuery("");
				setHighlightedIndex(-1);
				inputRef.current?.blur();
				break;

			case "Home":
				e.preventDefault();
				setHighlightedIndex(0);
				break;

			case "End":
				e.preventDefault();
				const lastIndex = displayedOptions.length - 1;
				setHighlightedIndex(lastIndex);
				// Load more if needed
				if (hasMore && lastIndex >= displayedCount - 1) {
					setDisplayedCount((count) => count + LOAD_MORE_COUNT);
				}
				break;

			default:
				// Allow typing to search
				break;
		}
	};

	// Handle clear
	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange?.("");
		setSearchQuery("");
		setDisplayedCount(INITIAL_LOAD_COUNT);
	};

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				inputRef.current &&
				!inputRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
				setSearchQuery("");
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, [isOpen]);

	// Focus input when dropdown opens
	useEffect(() => {
		if (isOpen && inputRef.current) {
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	}, [isOpen]);

	// Get display value
	const displayValue = value || "";

	return (
		<div className={styles.container}>
			<div
				className={`${styles.inputWrapper} cursor-pointer`}
				onClick={() => setIsOpen(true)}
			>
				<input
					ref={inputRef}
					type="text"
					className={styles.input}
					placeholder="Select County, State"
					value={isOpen ? searchQuery : displayValue}
					data-testid="county-state-filter-input"
					onChange={(e) => {
						setSearchQuery(e.target.value);
						setIsOpen(true);
					}}
					onFocus={() => setIsOpen(true)}
					onKeyDown={handleKeyDown}
					readOnly={!isOpen}
				/>
				<div className={styles.icons}>
					{displayValue && !isOpen && (
						<button
							type="button"
							onClick={handleClear}
							className={`${styles.clearButton} cursor-pointer`}
							aria-label="Clear selection"
						>
							<X className={styles.clearIcon} />
						</button>
					)}
					<ChevronDown
						className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`}
					/>
				</div>
			</div>

			{isOpen && (
				<div ref={dropdownRef} className={styles.dropdown}>
					{filteredOptions.length === 0 ? (
						<div className={styles.noResults}>
							No counties found matching &quot;{searchQuery}
							&quot;
						</div>
					) : (
						<>
							<div
								ref={scrollContainerRef}
								className={styles.optionsList}
								onScroll={handleScroll}
							>
								{displayedOptions.map((option, index) => (
									<button
										key={option.value}
										ref={(el) => {
											optionRefs.current[index] = el;
										}}
										type="button"
										data-testid="county-state-option-button"
										className={`${styles.option} ${
											option.value === value
												? styles.optionSelected
												: ""
										} ${
											index === highlightedIndex
												? styles.optionHighlighted
												: ""
										} cursor-pointer`}
										onClick={() =>
											handleSelect(option.value)
										}
										onMouseEnter={() =>
											setHighlightedIndex(index)
										}
									>
										{option.label}
									</button>
								))}
							</div>
							{hasMore && (
								<div className={styles.loadMoreIndicator}>
									Scroll for more results (
									{filteredOptions.length -
										displayedOptions.length}{" "}
									remaining)
								</div>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
}
